import React, { useMemo } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'

import {
  useParams,
  Routes,
  Route,
  useNavigate,
  useMatch,
} from 'react-router-dom'

import { lazy as loadable } from '@loadable/component'

import { YStack, XStack, Group } from 'tamagui'

import { GuildMetadata } from '@components'
import { H2, Button, wrapSuspenseComponent } from '@newComponents'

// import { GuildEventsScreenUpcomingEventsCard } from './GuildEventsScreenUpcomingEventsCard'
// import { GuildEventsScreenPastEventsCard } from './GuildEventsScreenPastEventsCard'

import { GuildEventsScreenQuery } from './__generated__/GuildEventsScreenQuery.graphql'

const GuildEventsUpcomingScreen = wrapSuspenseComponent(
  loadable(
    () => import('@screens/GuildEventsUpcomingScreen/GuildEventsUpcomingScreen')
  )
)

const GuildEventsPastScreen = wrapSuspenseComponent(
  loadable(() => import('@screens/GuildEventsPastScreen/GuildEventsPastScreen'))
)

export const GuildEventsScreen = () => {
  const params = useParams()

  const upcomingMatch = useMatch(`${params.actorId}/events/upcoming`)
  const pastMatch = useMatch(`${params.actorId}/events/past`)

  const data = useLazyLoadQuery<GuildEventsScreenQuery>(
    graphql`
      query GuildEventsScreenQuery($id: String!) {
        nodeBySlugId(id: $id) {
          __typename

          ... on Guild {
            name

            ...GuildMetadata_guild

            upcomingEventsCount: eventsByTimeSelection(
              first: 1
              timeSelection: UPCOMING
              orderBy: [START_AT_DESC, ID_ASC]
            ) {
              totalCount
            }

            pastEventsCount: eventsByTimeSelection(
              first: 1
              timeSelection: PAST
              orderBy: [START_AT_DESC, ID_ASC]
            ) {
              totalCount
            }
          }
        }
      }
    `,
    {
      id: params.actorId,
    }
  )

  const navigate = useNavigate()

  const upcomingOrPast = useMemo(() => {
    if (upcomingMatch) {
      return 'upcoming'
    }

    if (pastMatch) {
      return 'past'
    }

    if (data.nodeBySlugId?.__typename !== 'Guild') {
      return 'upcoming'
    }

    if (
      !data.nodeBySlugId.upcomingEventsCount.totalCount &&
      data.nodeBySlugId.pastEventsCount.totalCount
    ) {
      return 'past'
    }

    return 'upcoming'
  }, [upcomingMatch, pastMatch, data])

  if (data.nodeBySlugId?.__typename !== 'Guild') {
    return null
  }

  return (
    <>
      <GuildMetadata
        guild={data.nodeBySlugId}
        title={`${data.nodeBySlugId.name} ${
          upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'
        } Events`}
      />

      <YStack gap='$4' flex={1}>
        <XStack alignItems='center' gap='$4'>
          <XStack flex={1}>
            <H2>
              {upcomingOrPast === 'upcoming' ? 'Upcoming' : 'Past'} Events
            </H2>
          </XStack>

          <Group orientation='horizontal'>
            <Group.Item>
              <Button
                tag='a'
                href={`/${params.actorId}/events/upcoming`}
                secondary={upcomingOrPast !== 'upcoming'}
                onPress={(event) => {
                  event.preventDefault()
                  event.stopPropagation()

                  navigate('upcoming')
                }}
                successFlash={false}
                errorFlash={false}
              >
                <Button.Text
                  size='$4'
                  secondary={upcomingOrPast !== 'upcoming'}
                >
                  Upcoming
                </Button.Text>
              </Button>
            </Group.Item>
            <Group.Item>
              <Button
                tag='a'
                href={`/${params.actorId}/events/past`}
                secondary={upcomingOrPast !== 'past'}
                onPress={(event) => {
                  event.preventDefault()
                  event.stopPropagation()

                  navigate('past')
                }}
                successFlash={false}
                errorFlash={false}
              >
                <Button.Text size='$4' secondary={upcomingOrPast !== 'past'}>
                  Past
                </Button.Text>
              </Button>
            </Group.Item>
          </Group>
        </XStack>

        <Routes>
          {!data.nodeBySlugId.upcomingEventsCount.totalCount &&
          data.nodeBySlugId.pastEventsCount.totalCount ? (
            <Route index element={<GuildEventsPastScreen />} />
          ) : (
            <Route index element={<GuildEventsUpcomingScreen />} />
          )}

          <Route path='upcoming' element={<GuildEventsUpcomingScreen />} />

          <Route path='past' element={<GuildEventsPastScreen />} />
        </Routes>
      </YStack>
    </>
  )
}

export default GuildEventsScreen
